import { useRollbar } from '@rollbar/react'
import { mercadoCreditCardTypes } from 'common/enums/PaymentMethodEnum'
import loadScript from 'publisher/utils/loadScript'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import Rollbar from 'rollbar'
import { useOfferPricing } from 'src/publisher/hooks/use-offer-pricing'
import { centsToEuro } from 'src/shared/utils/price-calculator'
import { usePayment } from '../store'
import {
  getMercadoPagoPublicKey,
  getPaymentMethods,
} from '../store/payment/paymentSelectors'
import { useAreInstallmentsVisible } from './mercadoPago/hooks/useAreInstallmentsVisible'
import {
  MercadoPagoCardFormInterface,
  MercadoPagoFieldErrorsInterface,
  MercadoPagoInterface,
} from './mercadoPago/types/MercadoPagoInterface'
import { initCardForm } from './mercadoPago/utils/initCardForm'
import { useCardErrors } from './shared/hooks/useCardErrors'

type InstallmentsParams = {
  amount: string
  locale?: string
  bin: string
  processingMode: string
}

export type MercadoPago = {
  cardForm: (e: any) => any
  getInstallments: (params: InstallmentsParams) => Promise<any>
}

export interface MeracdoErrorCause {
  code: string
  description: string
}

export type MercadoPagoServerError = {
  cause: MeracdoErrorCause[]
  error: string
  message: string
  status: number
}

interface Constructable<T> {
  new (key: string, options?: { locale: string }): T
}

declare global {
  interface Window {
    MercadoPago: Constructable<MercadoPago>
    cardPaymentBrickController: {
      unmount: () => void
      getFormData: () => {
        installments: number
        issuer_id: string
        payer: {
          email: string
          identification: {
            type: string
            number: string
          }
        }
        payment_method_id: string
        token: string
        transaction_amount: number
      }
    }
    MP_DEVICE_SESSION_ID: string
    Rollbar?: Rollbar
  }
}

const defaultErrors: MercadoPagoFieldErrorsInterface = {
  cardNumber: '',
  cardExpiration: '',
  cardholderName: '',
  identificationNumber: '',
  securityCode: '',
}

const MercadoPagoContext = createContext<MercadoPagoInterface>(
  {} as MercadoPagoInterface,
)

export const useMercadoPago = () => useContext(MercadoPagoContext)

export function MercadoPagoProvider(
  props: React.PropsWithChildren<Record<string, never>>,
) {
  const rollbar = useRollbar()
  const { t } = useTranslation('publisher')
  const paymentMethods = usePayment(getPaymentMethods)
  const mercadoPagoPublicKey = usePayment(getMercadoPagoPublicKey)
  const [mercadoPago, setMercadoPago] = useState<MercadoPago | null>(null)
  const [initMercadoPago, setInitMercadoPago] = useState<boolean>(false)
  const [cardForm, setCardForm] = useState<MercadoPagoCardFormInterface | null>(
    null,
  )
  const { addError, resetError, resetErrors, errors } =
    useCardErrors<MercadoPagoFieldErrorsInterface>(defaultErrors)

  const { selectedOfferPricing } = useOfferPricing()

  const areInstallmentsVisible = useAreInstallmentsVisible()
  const priceAmount = centsToEuro(selectedOfferPricing?.grossAmount ?? 0)

  useEffect(() => {
    if (mercadoPago && initMercadoPago && priceAmount !== null) {
      const cf = initCardForm(
        mercadoPago,
        priceAmount,
        addError,
        resetError,
        t,
        rollbar,
      )
      setCardForm(cf)

      return () => {
        try {
          cf.unmount()
        } catch (err) {
          rollbar.error('failed to unmount mercado pago card form', { err })
        }
      }
    }
  }, [mercadoPago, priceAmount, initMercadoPago])

  useEffect(() => {
    if (
      mercadoCreditCardTypes.some(paymentMethod =>
        paymentMethods.includes(paymentMethod),
      ) &&
      mercadoPagoPublicKey &&
      initMercadoPago
    ) {
      loadScript(
        process.env.MERCADO_PAGO_API_URL as string,
        'mercado-pago',
        async () => {
          const mp = new window.MercadoPago(mercadoPagoPublicKey)
          setMercadoPago(mp)
        },
      )
      loadScript(
        'https://www.mercadopago.com/v2/security.js',
        'mercado-pago-device-id',
        undefined,
        {
          view: 'checkout',
          output: 'MP_DEVICE_SESSION_ID',
        },
      )
    }
  }, [initMercadoPago])

  const generatePaymentData = useCallback(async () => {
    if (cardForm) {
      resetErrors()
      await cardForm.createCardToken()
      return cardForm.getCardFormData()
    }
  }, [cardForm])

  return (
    <MercadoPagoContext.Provider
      value={{
        cardForm,
        errors,
        resetError,
        generatePaymentData,
        areInstallmentsVisible,
        setInitMercadoPago,
      }}
    >
      {props.children}
    </MercadoPagoContext.Provider>
  )
}
